import React from 'react';
import { Info, Grid, Palette, Droplet, Package, Search, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о плитке и советами по выбору и укладке
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-fuchsia-500" />
        Всё о керамической плитке: виды, характеристики, выбор
      </h2>
      
      <div className="prose prose-fuchsia dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Grid className="w-5 h-5 text-fuchsia-500" />
          Что такое керамическая плитка?
        </h3>
        <p>
          <strong>Керамическая плитка</strong> — это строительный материал, изготовленный из глины и других природных материалов, обожженных при высоких температурах. Используется для облицовки стен, полов, фасадов зданий и других поверхностей благодаря своей прочности, долговечности и эстетическим качествам.
        </p>

        <h3 className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-fuchsia-500" />
          Основные виды плитки
        </h3>
        <ul>
          <li>
            <strong>Керамическая плитка</strong> — наиболее распространенный вариант, используется преимущественно для стен. Имеет глазурованную поверхность, доступна в широком ассортименте цветов и фактур.
          </li>
          <li>
            <strong>Керамогранит</strong> — более прочный материал, изготавливается методом прессования и обжига при более высоких температурах. Имеет низкое водопоглощение, морозоустойчив и износостоек. Подходит для пола и наружной отделки.
          </li>
          <li>
            <strong>Клинкерная плитка</strong> — отличается повышенной прочностью и морозостойкостью. Чаще используется для облицовки фасадов и уличных дорожек.
          </li>
          <li>
            <strong>Мозаика</strong> — мелкоформатная плитка, закрепленная на сетчатой основе. Удобна для облицовки сложных поверхностей и создания декоративных акцентов.
          </li>
          <li>
            <strong>Натуральный камень</strong> — гранит, мрамор, травертин и другие природные материалы. Обладают уникальным рисунком и текстурой, но требуют особого ухода.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Droplet className="w-5 h-5 text-fuchsia-500" />
          Ключевые характеристики плитки
        </h3>
        <ul>
          <li><strong>Водопоглощение:</strong> Показывает, сколько воды может впитать плитка. Для ванных комнат и кухонь рекомендуется использовать плитку с низким водопоглощением (до 3%).</li>
          <li><strong>Износостойкость:</strong> Определяется по шкале PEI от 1 до 5. Для жилых помещений подходит плитка 2-3 класса, для коммерческих — 4-5.</li>
          <li><strong>Морозостойкость:</strong> Важна для наружных работ. Измеряется количеством циклов замораживания-оттаивания, которые может выдержать плитка.</li>
          <li><strong>Твердость поверхности:</strong> По шкале Мооса от 1 до 10. Керамогранит обычно имеет твердость 6-7.</li>
          <li><strong>Сопротивление скольжению:</strong> Особенно важно для напольной плитки. Измеряется коэффициентом R (R9-R13).</li>
        </ul>

        <div className="p-4 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 my-6">
          <h4 className="text-fuchsia-700 dark:text-fuchsia-300 font-medium">Как правильно рассчитать количество плитки?</h4>
          <p className="text-fuchsia-600 dark:text-fuchsia-400 text-sm mb-0">
            При расчете учитывайте не только общую площадь, но и схему укладки. Для прямой укладки добавьте 10% запаса, для диагональной — 15-20%. Не забудьте исключить из расчета площадь дверных и оконных проемов, но добавьте плитку для откосов, если они есть.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Package className="w-5 h-5 text-fuchsia-500" />
          Дополнительные материалы для укладки
        </h3>
        <ul>
          <li>
            <strong>Плиточный клей:</strong> Выбирайте тип клея в зависимости от вида плитки и поверхности. Для керамогранита и крупноформатной плитки требуется усиленный клей.
          </li>
          <li>
            <strong>Затирка:</strong> Доступна в разных цветах и с разными свойствами (водоотталкивающая, противогрибковая и т.д.). Ширина шва определяет расход.
          </li>
          <li>
            <strong>Грунтовка:</strong> Необходима для обработки поверхности перед укладкой для лучшего сцепления и предотвращения появления плесени.
          </li>
          <li>
            <strong>Крестики и клинья:</strong> Используются для соблюдения одинаковой ширины швов между плитками.
          </li>
          <li>
            <strong>Инструменты:</strong> Плиткорез, шпатели, уровень, затирочная резиновая терка, губка и другие специальные инструменты.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Search className="w-5 h-5 text-fuchsia-500" />
          Как выбрать плитку для разных помещений
        </h3>
        
        <h4>Ванная комната</h4>
        <ul>
          <li>Низкое водопоглощение (до 3%)</li>
          <li>Противоскользящая поверхность для пола</li>
          <li>Керамическая плитка или керамогранит</li>
          <li>Рекомендуемые размеры для небольших ванных: 20×20 см, 20×30 см</li>
        </ul>
        
        <h4>Кухня</h4>
        <ul>
          <li>Стойкость к пятнам и легкость очистки</li>
          <li>Для фартука: керамическая глазурованная плитка</li>
          <li>Для пола: керамогранит с классом износостойкости PEI 3-4</li>
          <li>Популярные размеры для фартука: 10×20 см, 10×30 см</li>
        </ul>
        
        <h4>Гостиная и коридор</h4>
        <ul>
          <li>Для пола: керамогранит с классом износостойкости не ниже PEI 3</li>
          <li>Крупный формат визуально расширяет пространство: 60×60 см, 30×60 см</li>
        </ul>
        
        <h4>Уличные пространства</h4>
        <ul>
          <li>Высокая морозостойкость (не менее 50 циклов)</li>
          <li>Низкое водопоглощение (до 0.5% для керамогранита)</li>
          <li>Высокая стойкость к скольжению (R11-R13)</li>
          <li>Клинкерная плитка или уличный керамогранит</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при покупке плитки
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Проверяйте целостность упаковки и отсутствие сколов</li>
            <li>Покупайте плитку одной партии (тона и калибра)</li>
            <li>Уточняйте класс износостойкости и коэффициент скольжения</li>
            <li>Запрашивайте сертификаты соответствия для подтверждения качества</li>
            <li>Сохраняйте упаковку и этикетки для возможного добора плитки</li>
          </ul>
        </div>

        <h3>Схемы укладки плитки</h3>
        <ul>
          <li>
            <strong>Прямая (шов в шов)</strong> — классическая укладка, при которой швы образуют прямые линии. Самый простой и экономичный способ.
          </li>
          <li>
            <strong>Диагональная</strong> — плитка укладывается под углом 45° к стенам. Визуально расширяет пространство, но требует больше материала (+15-20%).
          </li>
          <li>
            <strong>В разбежку (кирпичная кладка)</strong> — каждый новый ряд смещается на 1/2 или 1/3 плитки. Популярна для прямоугольной плитки.
          </li>
          <li>
            <strong>Елочка</strong> — плитки укладываются под углом 45° друг к другу, образуя V-образный узор. Часто используется для плитки формата "кабанчик".
          </li>
          <li>
            <strong>Модульная</strong> — комбинирование плиток разных размеров по определенной схеме.
          </li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор расхода плитки поможет вам точно рассчитать необходимое количество материалов и спланировать бюджет для ремонта!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;